import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useNow } from '../../hooks/useNow';
import { useCompactMotion } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { closedDaysLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { toMinutes, windowFor } from '../../core/rules/schedule';
import type { OpeningDay } from '../../core/types/site';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';

/** Tijdelijk sfeerbeeld, niet de salon zelf: vervangen vóór livegang (docs/BEELDEN.md). */
const ROOM = 'https://cdn.magicpatterns.com/patterns/generated-images/7166f87a-c402-483f-8556-b060455341f8.jpg';

/**
 * "Kom langs": het laatste blok van de homepage, op blush. Het beeld staat in een boog, als
 * een deuropening die bij het scrollen opengaat: smal → volle boog, terwijl het beeld uitzoomt.
 * Daarna komen adres en knop binnen. Bij "minder beweging" staat de boog meteen open.
 * Een kleine regel zegt of er vandaag afspraken zijn (de salon werkt alleen op afspraak).
 * Draagt ook de lokale vindbaarheid (stad in de kop, adres als tekst).
 * Adres en dagen komen uit Instellingen › Salon & contact en › Salondagen; de woorden en het beeld uit Paginateksten.
 */
export function HomeVisit() {
  const pages = usePages();
  const ref = useRef<HTMLDivElement>(null);
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const now = useNow();
  const copy = pages.home.visit;
  const media = mediaOr(copy.media, {
    image: ROOM,
    alt: 'Sfeerbeeld: een behandelbed met linnen in daglicht'
  });
  const {
    openingHours
  } = useSite();
  const closed = closedDaysLabel(openingHours);
  const status = todayStatus(openingHours, now);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.6], [1.15, 1]);
  // De boog opent als een deur: van smal naar zijn volle vorm, klaar als hij midden in beeld staat.
  const compact = useCompactMotion();
  const side = compact ? 14 : 28;
  const clipPath = useTransform(scrollYProgress, [0.05, 0.5], [`inset(0% ${side}% 0% ${side}% round 9999px 9999px 0 0)`, 'inset(0% 0% 0% 0% round 9999px 9999px 0 0)']);
  return <section className="bg-blush" aria-labelledby="home-visit" data-id="element-7546" data-mp="negwst4xmxn5">
      <div className="mx-auto grid max-w-content gap-12 px-6 py-20 sm:py-24 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10 lg:py-32" data-id="element-7547" data-mp="2t6dy2jjng54a">
        <div ref={ref} className="mx-auto w-full max-w-xs sm:max-w-sm lg:col-span-5 lg:max-w-none" data-id="element-7548" data-mp="2p5ty787oyneg">
          {/* clip-path i.p.v. breedte: het beeld vervormt niet en de pagina verspringt niet. */}
          <motion.div style={reduce ? undefined : {
          clipPath
        }} className="relative aspect-[4/5] w-full overflow-hidden rounded-t-full bg-sand" data-id="element-7549" data-mp="2j1dzw8clxxlx">
            <motion.div style={reduce ? undefined : {
            scale
          }} className="absolute inset-0" data-id="element-7550" data-mp="2597u9g72z90q">
              <MediaView media={media} sizes="(min-width: 1024px) 38vw, 100vw" className="h-full w-full" data-id="element-7551" data-mp="2a80wgcgx3dll" />
            </motion.div>
          </motion.div>
        </div>

        {/* Adres en knop komen binnen als de boog bijna open is, licht van rechts. */}
        <motion.div className="lg:col-span-7 lg:pb-4" initial={reduce ? false : {
        opacity: 0,
        x: 24
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        amount: 0.35
      }} transition={{
        duration: 0.3,
        delay: 0.1,
        ease
      }} data-id="element-7552" data-mp="1md80bzujlvei">
          <p className="flex items-center gap-2.5 text-[14px] text-ink" data-id="element-7553" data-mp="3lpk7gr0qokup">
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${status.open ? 'bg-sage' : 'bg-ink/30'}`} data-id="element-7554" data-mp="1ln12f1dwjkz7" />
            {status.label}
          </p>
          <h2 id="home-visit" className={`mt-5 ${sectionHeading} text-ink`} data-id="element-7555" data-mp="1gohi15eyih8y">
            <HeadingText heading={copy.heading} accentBlock data-id="element-7556" data-mp="1bntrslyjy4fs" />
          </h2>
          {copy.body && <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-7557" data-mp="17o1gw2lorm79">{fill(copy.body)}</p>}

          <address className="mt-8 flex max-w-lg gap-3 border-t border-ink/15 pt-6 text-[15px] not-italic leading-relaxed text-ink" data-id="element-7558" data-mp="12ogf7kf4szqu">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-7559" data-mp="xvvmynbviupf" />
            <span data-id="element-7560" data-mp="syn4smqq6qo6">
              {salon.street}, {salon.postalCode} {salon.locality}
              <span className="block text-muted" data-id="element-7561" data-mp="o0vefx5qbcm3">
                Alleen op afspraak{closed ? ` · ${closed.toLowerCase()}` : ''}
              </span>
            </span>
          </address>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6" data-id="element-7562" data-mp="ezx99bjk0z1x">
            {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-ink px-7 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98]" data-id="element-7563" data-mp="2qo226pl7kcae">
                {fill(copy.cta)}
              </button>}
            {copy.secondaryCta && <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7564" data-mp="2bxbcbn36fn9x">
                {fill(copy.secondaryCta)}
                <span className="sr-only" data-id="element-7565" data-mp="2lneg8ibu6ln1"> {shopLabels.newTab}</span>
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7566" data-mp="2lopjmh1ddrdm" />
              </a>}
          </div>
          <Link to="/contact" className="mt-2 inline-flex min-h-[44px] items-center text-[14px] text-muted underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7567" data-mp="1t5sahjkw2f9h">
            Openingstijden, parkeren en aanbellen
          </Link>
        </motion.div>
      </div>
    </section>;
}

/**
 * De salon werkt alleen op afspraak, dus nooit "open": wel wanneer er vandaag afspraken zijn.
 * "Vandaag op afspraak, 13:00 – 20:00" / "Vandaag geen afspraken meer" / "Vandaag gesloten". Puur.
 */
function todayStatus(hours: OpeningDay[], now: Date): {
  open: boolean;
  label: string;
} {
  const w = windowFor(hours, now.getDay());
  if (!w) return {
    open: false,
    label: 'Vandaag gesloten'
  };
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes >= toMinutes(w.close)) return {
    open: false,
    label: 'Vandaag geen afspraken meer'
  };
  return {
    open: true,
    label: `Vandaag op afspraak, ${w.open} – ${w.close}`
  };
}